import { Controller } from "stimulus"

export default class extends Controller {
  private onDocumentClick = (event: MouseEvent): void => {
    if ((event.target as HTMLElement | null)?.closest(".inbox-dd")) return

    this.closeAll()
  }

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.closeAll()
  }

  connect(): void {
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKeydown)
    this.dropdowns().forEach(dropdown => { this.refreshTrigger(dropdown) })
  }

  disconnect(): void {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  click(event: MouseEvent): void {
    const trigger = (event.target as HTMLElement | null)?.closest(".inbox-dd-trigger")
    if (!trigger) return

    event.preventDefault()
    event.stopPropagation()

    const dropdown = trigger.closest<HTMLElement>(".inbox-dd")!
    const wasOpen = dropdown.classList.contains("open")

    this.closeAll()
    if (!wasOpen) this.open(dropdown)
  }

  pick(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.type !== "radio") return

    const dropdown = input.closest<HTMLElement>(".inbox-dd")
    if (dropdown) this.refreshTrigger(dropdown)

    // Company narrows which users exist, so carrying the old one across would submit a user the
    // new list does not contain. Clearing it first lands on the page's default instead.
    if (input.name === "company_id") this.clear("as_user")

    this.closeAll()
    ;(this.element as HTMLFormElement).submit()
  }

  search(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.closest(".inbox-dd-search")) return

    const menu = input.closest<HTMLElement>(".inbox-dd-menu")
    if (!menu) return

    const query = input.value.trim().toLowerCase()
    let matches = 0

    menu.querySelectorAll<HTMLElement>(".inbox-dd-opt").forEach(option => {
      if (option.classList.contains("inbox-dd-all")) return

      const hit = query === "" || this.label(option).toLowerCase().includes(query)
      option.classList.toggle("d-none", !hit)
      if (hit) matches += 1
    })

    menu.querySelector(".inbox-dd-empty")?.classList.toggle("d-none", matches > 0)
  }

  // --- internals ---------------------------------------------------------

  private dropdowns(): HTMLElement[] {
    return Array.from(this.element.querySelectorAll<HTMLElement>(".inbox-dd"))
  }

  private open(dropdown: HTMLElement): void {
    dropdown.classList.add("open")
    dropdown.querySelector(".inbox-dd-trigger")?.setAttribute("aria-expanded", "true")

    const search = dropdown.querySelector<HTMLInputElement>(".inbox-dd-search input")
    if (!search) return

    search.value = ""
    search.dispatchEvent(new Event("input", { bubbles: true }))
    search.focus()
  }

  private closeAll(): void {
    this.dropdowns().forEach(dropdown => {
      dropdown.classList.remove("open")
      dropdown.querySelector(".inbox-dd-trigger")?.setAttribute("aria-expanded", "false")
    })
  }

  private clear(name: string): void {
    const fallback = this.element.querySelector<HTMLInputElement>(`input[name="${name}"][value="all"]`)
    if (fallback) fallback.checked = true

    const dropdown = fallback?.closest<HTMLElement>(".inbox-dd")
    if (dropdown) this.refreshTrigger(dropdown)
  }

  // The "all" radio means "no filter", not a selection -- same rule the Inbox's own
  // refreshTrigger applies.
  private refreshTrigger(dropdown: HTMLElement): void {
    const checked = dropdown.querySelector<HTMLInputElement>(".inbox-dd-menu input:checked:not([value='all'])")
    const allLabel = dropdown.dataset.allLabel || "All"
    const option = checked?.closest<HTMLElement>(".inbox-dd-opt")

    const labelEl = dropdown.querySelector(".inbox-dd-label")
    if (labelEl) labelEl.textContent = option ? this.label(option) : allLabel

    dropdown.querySelector(".inbox-dd-trigger")?.classList.toggle("has-val", !!checked)
    dropdown.querySelector(".inbox-dd-all")?.classList.toggle("active", !checked)
  }

  private label(option: HTMLElement): string {
    return option.querySelector(".inbox-dd-opt-label")?.textContent?.trim() || ""
  }
}
